
import React, { useEffect, useState } from 'react';
import { Card, TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody, Collapse, IconButton, Typography, Box, Button, Tooltip } from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import useApi from '../../../shared/hooks/useApi';
import dayjs from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import * as XLSX from 'xlsx';

const AfrrReservation = ({ colors, setLoading, triggerAlert }) => {
    const { apiGet } = useApi();
    const [afrrReservations, setAfrrReservations] = useState([]);
    const [afrrProposals, setAfrrProposals] = useState([]);
    const [mergedData, setMergedData] = useState([]);
    const [expandedRows, setExpandedRows] = useState({});
    const [dateFrom, setDateFrom] = useState(dayjs().subtract(7, 'day'));
    const [dateTo, setDateTo] = useState(dayjs());
   
    useEffect(() => {
        setLoading(true);
        const getDataRoute = `/esid-24-003/get-afrr-reservations-by-date?from=${dateFrom.format('YYYY-MM-DD')}&to=${dateTo.format('YYYY-MM-DD')}`;
        const getDataProposalRoute = `/esid-24-003/get-afrr-proposals-by-date?from=${dateFrom.format('YYYY-MM-DD')}&to=${dateTo.format('YYYY-MM-DD')}`;

        const fetchAll = async () => {
            try {
 
                const [reservationsResp, proposalsResp] = await Promise.allSettled([
                    apiGet("UX_HTTP_API", getDataRoute),
                    apiGet("UX_HTTP_API", getDataProposalRoute)
                ]);

                const parseList = (resp) => {
                    const data = resp?.status === 'fulfilled' ? resp.value : undefined;
                    return Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
                };

                const reservationsList = parseList(reservationsResp);
                const proposalsList = parseList(proposalsResp);


                const flattenedReservations = reservationsList.map((item, index) => {
                    const details = Array.isArray(item?.afrr_reservations) ? item.afrr_reservations : [];
                    const volume = details.reduce((acc, curr) => acc + (parseFloat(curr?.afrr_down_capacity_reserved) || 0), 0);
                    return { id: index + 1, date: item?.date, volume, price: 'N/A', details };
                });

                const flattenedProposals = proposalsList.map((item, index) => {
                    const details = Array.isArray(item?.afrr_reservations) ? item.afrr_reservations : [];
                    const volume = details.reduce((acc, curr) => acc + (parseFloat(curr?.afrr_down_capacity_reserved) || 0), 0);
                    return { id: index + 1, date: item?.date, volume, price: 'N/A', details };
                });

              

                if (flattenedReservations.length > 0) setAfrrReservations(flattenedReservations);
                if (flattenedProposals.length > 0) setAfrrProposals(flattenedProposals);

                if (flattenedReservations.length === 0 && flattenedProposals.length === 0) {
                    triggerAlert?.('warning', 'Seçili tarih aralığında veri bulunamadı. Mevcut veri korunuyor.');
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                triggerAlert?.('error', 'Veri alınırken hata oluştu.');
                // Mevcut state korunur
            } finally {
                setLoading(false);
            }
        };

        fetchAll();
    }, [dateFrom, dateTo]);

    function normalizeDate(dateStr) {
        // Convert date string to ISO format (timezone differences are not important)
        return new Date(dateStr).toISOString(); // example: "2025-05-12T22:00:00.000Z"
      }
      
      function mergeDataArrays(data1, data2) {
        const result = [];
        const map = new Map();
      
        for (const item of data1) {
          map.set(item.date, { ...item, details: [...item.details] });
        }
      
        for (const item of data2) {
          if (!map.has(item.date)) {
            map.set(item.date, { ...item, details: [...item.details] });
          } else {
            const existing = map.get(item.date);
      
            // Match by normalized "from" date
            const detailMap = new Map();
            for (const detail of existing.details) {
              detailMap.set(normalizeDate(detail.from), { ...detail });
            }
      
            for (const detail of item.details) {
              const key = normalizeDate(detail.from);
              if (detailMap.has(key)) {
                const existingDetail = detailMap.get(key);
                // Keep "from" and "to" as-is, merge other keys
                detailMap.set(key, {
                  ...existingDetail,
                  ...detail,
                  from: existingDetail.from, // preserve original 'from' value
                  to: existingDetail.to ?? detail.to // if 'to' is missing, take from the other
                });
              } else {
                detailMap.set(key, { ...detail });
              }
            }
      
            existing.details = Array.from(detailMap.values());
            map.set(item.date, existing);
          }
        }
        const merged = Array.from(map.values());
      
        return merged;
      }


    useEffect(() => {
        // Merge when either list has items
        if (afrrProposals.length > 0 || afrrReservations.length > 0) {
            const normalizedData = mergeDataArrays(afrrReservations, afrrProposals);
            setMergedData(normalizedData);
            // Detect missing days vs filter
            try {
                const fromDate = dayjs(dateFrom.format('YYYY-MM-DD'));
                const toDate = dayjs(dateTo.format('YYYY-MM-DD'));
                const days = [];
                let cursor = fromDate.clone();
                while (cursor.isBefore(toDate) || cursor.isSame(toDate, 'day')) {
                    days.push(cursor.format('YYYY-MM-DD'));
                    cursor = cursor.add(1, 'day');
                }
                const present = new Set(normalizedData.map(d => dayjs(d.date).format('YYYY-MM-DD')));
                const missing = days.filter(d => !present.has(d));
            } catch (e) {
            }
        }
    }, [afrrProposals, afrrReservations]);

    const handleRowClick = (index) => {
        setExpandedRows(prevState => ({
            ...prevState,
            [index]: !prevState[index]
        }));
    };

    const handleDownloadSingle = (reservation) => {
        try {
            const headers = ['From', 'To', 'FSP confirmed', 'Up Capacity Reserved', 'Capacity Volume Down', 'Minimum Capacity Price Down'];
            const data = reservation.details.map(detail => [detail.from, detail.to, detail.afrr_down_capacity_reserved, detail.afrr_up_capacity_reserved, detail.capacity_volume_down, detail.min_cap_price_aFRR_down]);
            const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Reservation");
            // Write file
            XLSX.writeFile(wb, `reservation_${reservation.id}.xlsx`);
            triggerAlert("success", "Reservation downloaded successfully");
        } catch (error) {
            console.error('Error downloading single reservation:', error);
            triggerAlert("error", "Error downloading single reservation");
        } finally {
            setLoading(false);
        }

    };

    const handleDownloadAll = () => {
        try {
            const headers = ['ID',  'From', 'To', 'FSP confirmed', 'Up Capacity Reserved', 'Capacity Volume Down', 'Minimum Capacity Price Down'];
            
            const data = mergedData.flatMap(reservation =>
                reservation.details.map(detail => [
                    reservation.id,
                    detail.from,
                    detail.to,
                    detail.afrr_down_capacity_reserved,
                    detail.afrr_up_capacity_reserved,
                    detail.capacity_volume_down,
                    detail.min_cap_price_aFRR_down
                ])
            );

            const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "All Reservations");
            // Write file
            XLSX.writeFile(wb, `all_reservations_${dateFrom}_${dateTo}.xlsx`);
            triggerAlert("success", "All reservations downloaded successfully");
        } catch (error) {
            console.error('Error downloading all reservations:', error);
            triggerAlert("error", "Error downloading all reservations");
        } finally {
            setLoading(false);
        }
    };
    const formatStringDate = (date) => {
        if (typeof date === 'string') return date.split('-').reverse().join('-');
        try { return dayjs(date).isValid() ? dayjs(date).format('DD-MM-YYYY') : ''; } catch { return ''; }
    }
    const formatDate = (date) => {

        return dayjs(date).format('DD-MM-YYYY HH:mm');
    }
    return (
        <>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4" color={colors.grey[100]}>
                    aFRR Report
                </Typography>
                <Box display="flex" gap={2} alignItems="center">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="From Date"
                            value={dateFrom}
                            onChange={(newValue) => setDateFrom(newValue)}
                            format='DD/MM/YYYY'
                            sx={{
                                '& .MuiInputBase-root': {
                                    color: colors.grey[100],
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: colors.grey[700]
                                    }
                                }
                            }}
                        />
                        <DatePicker
                            label="To Date"
                            value={dateTo}
                            onChange={(newValue) => setDateTo(newValue)}
                            format='DD/MM/YYYY'
                            sx={{
                                '& .MuiInputBase-root': {
                                    color: colors.grey[100],
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: colors.grey[700]
                                    }
                                }
                            }}
                        />
                    </LocalizationProvider>
                </Box>

                <Button
                    variant="contained"
                    startIcon={<FileDownloadIcon />}
                    onClick={handleDownloadAll}
                    sx={{
                        bgcolor: colors.entras_purple[100],
                        '&:hover': {
                            bgcolor: colors.entras_purple[200]
                        }
                    }}
                >
                    Download All
                </Button>
            </Box>
            <Card sx={{ bgcolor: colors.primary[400], boxShadow: 3 }}>


                <TableContainer component={Paper} sx={{ bgcolor: 'transparent' }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: "bold", width: "10%" }} />
                                <TableCell sx={{ fontWeight: "bold", width: "50%" }}>Name</TableCell>
                                <TableCell sx={{ fontWeight: "bold", width: "30%" }}>Date</TableCell>
                                <TableCell sx={{ fontWeight: "bold", width: "30%" }} align='center'>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {mergedData.map((reservation, index) => (
                                <React.Fragment key={index}>
                                    <TableRow>
                                        <TableCell>
                                            <IconButton
                                                aria-label="expand row"
                                                size="small"
                                                onClick={() => handleRowClick(index)}
                                            >
                                                {expandedRows[index] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>AFRR REPORT {formatStringDate(reservation.date)}</TableCell>
                                        <TableCell >{formatStringDate(reservation.date)}</TableCell>
                                        <TableCell align='center'>
                                            <Tooltip title="Download Row">
                                                <IconButton
                                                    onClick={() => handleDownloadSingle(reservation)}
                                                    sx={{ color: colors.grey[100] }}
                                                >
                                                    <FileDownloadIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>

                                    </TableRow>

                                    <TableRow>

                                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>

                                            <Collapse in={expandedRows[index]} timeout="auto" unmountOnExit>

                                                <Table size="small" aria-label="purchases">

                                                    <TableHead>

                                                        <TableRow sx={{}}>
                                                            <TableCell width="10%"></TableCell>

                                                            <TableCell width="20%" >From</TableCell>

                                                            <TableCell width="20%" >To</TableCell>

                                                            <TableCell width="20%"  >aFRR down capacity reserved (kW) - FSP confirmed</TableCell>
                                                            <TableCell width="20%" >aFRR down capacity proposal (kW) - Endustries proposal</TableCell>
                                                            <TableCell width="20%" >aFRR down minimum capacity price (€/MW/h) - Endustries proposal</TableCell>


                                                            {/*                                                             <TableCell>Up Capacity Reserved</TableCell>
 */}
                                                        </TableRow>

                                                    </TableHead>

                                                    <TableBody sx={{}}>

                                                        {reservation.details.map((detail, detailIndex) => (
                                                            <TableRow key={detailIndex} sx={{}}>
                                                                <TableCell width="10%" ></TableCell>
                                                                <TableCell width="20%" >{formatDate(detail.from)}</TableCell>
                                                                <TableCell width="20%" >{formatDate(detail.to)}</TableCell>
                                                                <TableCell width="20%" >{detail.afrr_down_capacity_reserved}</TableCell>
                                                                <TableCell width="20%" >{detail.capacity_volume_down}</TableCell>
                                                                <TableCell width="20%" >{detail.min_cap_price_aFRR_down}</TableCell>


                                                                {/*                                                                 <TableCell>{detail.afrr_up_capacity_reserved}</TableCell>
 */}
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>

                                                </Table>

                                            </Collapse>

                                        </TableCell>

                                    </TableRow>

                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>
        </>
    );

};



export default AfrrReservation;
